/* KANON `Programy v2 - D3 (0.8)` — the muscle split, transcribed.
 *
 * Read off the rendered specimen at 1512x1000, with the FIGURES grown a third by owner decision
 * D24 of 4 September 2026 -- the canon's placeholder body was too small to read as anatomy:
 *   section  0 19 padding, an 8px column, 172.5 tall including its eyebrow (canon: 137.5)
 *   body     17.5 between the figures and the readout, the readout centred against the figures
 *   figures  two 76.5-wide columns, 9.5 apart, each a 4px stack of SVG over caption (canon: 59)
 *   svg      76.5 x 138.5 (canon: 59 x 106.5)
 *   caption  9 / 700 / --ls-rail, uppercase, --text-3 (canon: 7, grown with the drawing it names)
 *   readout  the canon's 9.5px bar over its legend. It is a RANKING of six rows since 16
 *            September 2026 (owner) — see `MuscleRanking`
 *
 * ── The SECTION's reserve follows the figure, and it has to ──────────────────────────────────
 * `--h-pl-muscle` is a fixed height and `.body` is `flex: 1` inside it, so a figure column taller
 * than the box would overflow the card rather than push it. The token grew by exactly what the
 * figure column gained -- the 32px of SVG plus the caption's taller line -- which is also what
 * keeps the readout's `align-self: center` honest, because the space it centres in changed.
 *
 * ── The readout is CENTRED, not stacked from the top ─────────────────────────────────────────
 * The specimen puts the readout's block at exactly the vertical middle of the figures beside it, so
 * the two read as one object rather than as a caption under a drawing. `align-self: center` on a
 * block whose own height is its content's.
 *
 * ── The whole section reserves `--h-pl-muscle` ───────────────────────────────────────────────
 * Held from the first frame, so the strip below it never moves. Every state — the wait, an empty
 * split, six segments — stands inside that one box.
 *
 * **Every text leaf states its line-height.** `base.css` puts `--lh-relaxed` on `body` and the
 * specimen's body carries none, so an unstated leaf renders about 2px taller than the canon.
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js); every colour,
 * space, radius and type value below is a token. */

.section {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-2);
  height: var(--h-pl-muscle);
  padding: var(--space-0) var(--space-19px);
}

/* An `h3`, so the pane's two sections are landmarks a reader user can jump between. The heading's
 * user-agent margin and its user-agent size are both taken off here: the canon draws an eyebrow,
 * and nothing about the box may move because the element changed. */
.eyebrow {
  margin: var(--space-0);
  font-size: var(--fs-pl-eyebrow);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-eyebrow);
  color: var(--text-3);
  text-transform: uppercase;
}

.body {
  display: flex;
  flex: 1;
  gap: var(--space-17-5px);
  min-height: 0;
}

/* ── The pair of figures ────────────────────────────────────────────────────────────────────── */

.figures {
  display: flex;
  flex: none;
  gap: var(--space-9-5px);
}

/* `--h-silhouette` is the contract `BodySilhouette` reads for its own box; the width is this
 * surface's. Between them they give the specimen's 59 x 106.5 footprint. */
.figureBox {
  --h-silhouette: var(--h-pl-figure);

  /* NO ring on this figure. `BodySilhouette` draws one by default and its header says why; the
   * short of it is that the ring is there to rescue a categorical palette whose fills cannot be
   * chosen for contrast against each other. The four `--silhouette-*` steps ARE chosen for it, so
   * here the ring only competes: at 0.38 white it is brighter than the `mid` step, which outlined
   * every muscle at the strength of a worked one.
   *
   * The value arrives as a custom property because Stylelint's allowlist takes a token reference
   * and nothing else, and `transparent` is not one — a custom property is never read by that gate. */
  --silhouette-ring: transparent;

  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  align-items: center;
  width: var(--w-pl-figure);
}

.figureCaption {
  font-size: var(--fs-pl-caption);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-rail);
  color: var(--text-3);
  text-transform: uppercase;
}

/* ── The bar, its legend, and what stands there instead ─────────────────────────────────────── */

/* `min-height` is the RESERVE, and it is what `align-self: center` makes load-bearing: a centred
 * box's top is `(space − its own height) / 2`, so every pixel the readout gained as it filled
 * lifted the bar by half of it. Measured on a cold entry with `/full` delayed 400ms: the bar and
 * the legend stood 14.4px lower before the skeleton appeared and 1px lower again while it was up.
 * The reserve is the loaded one-line shape, which every state now stands inside. */
.readout {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2);
  align-self: center;
  min-width: 0;
  min-height: var(--h-pl-readout);
}

/* A statement of fact, in the section's own neutral ink — a plan with no catalogued exercises has
 * nothing to draw, and nothing has gone wrong. */
.empty {
  margin: var(--space-0);
  font-size: var(--fs-pl-body);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-3);
}

/* ── The wait ───────────────────────────────────────────────────────────────────────────────── */

/* The ranking's own column, at its own pitch, so the bars occupy exactly the box the rows will. A
 * skeleton that is shorter than what replaces it lifts a CENTRED readout by half the difference —
 * measured at 1px when the old legend's bars were two pixels short of its entries. */
.skelRanking {
  display: flex;
  flex-direction: column;
  gap: var(--space-5-5px);
}

/* Fourteen, which is what a ranked row occupies: an 11px label on the caption step's 14px line,
 * taller than the 9.5px track beside it. The widths taper so the block reads as a list rather than as a grid. */
.skelRow {
  height: var(--type-caption-lh);
}

.skelRanking .skelRow:nth-child(1) { width: 92%; }
.skelRanking .skelRow:nth-child(2) { width: 88%; }
.skelRanking .skelRow:nth-child(3) { width: 84%; }
.skelRanking .skelRow:nth-child(4) { width: 84%; }
.skelRanking .skelRow:nth-child(5) { width: 80%; }
.skelRanking .skelRow:nth-child(6) { width: 76%; }
